/**
 * Shared pure helpers for the inventory hub — labels, formatting, and cache invalidation used across
 * the On-hand table, the merged Short & reorder view, the draft PO, and the per-blank drawer. Kept
 * here (one place) so the four former per-page copies of blank-label / variant-label / negative-red /
 * money don't drift. Also the "Add stock" size-grid math (color-match + size ordering, unit-tested).
 */
import type { QueryClient } from '@tanstack/react-query';

/**
 * Anything with the S&S identity quad + sku (+ optional Shopify sample fallbacks for never-stocked
 * blanks) — the subset {@link blankLabel} / {@link variantLabel} read to name a physical blank. The
 * one home for this shape so the pull lists (inventory hub, press station, gang-sheet pick list) name
 * a blank the same way.
 */
export type BlankName = {
  ssSku: string;
  brandName?: string;
  styleName?: string;
  colorName?: string;
  sizeName?: string;
  sampleTitle?: string;
  sampleVariantTitle?: string;
};

/** Best product label: canonical S&S brand + style, else the demanding order's Shopify title, else the SKU. */
export function blankLabel(row: BlankName): string {
  const canonical = [row.brandName, row.styleName].filter(Boolean).join(' ');
  if (canonical) return canonical;
  if (row.sampleTitle) return row.sampleTitle;
  return row.ssSku;
}

/** Colour · size sub-label: canonical when known, else the Shopify variant title, else empty. */
export function variantLabel(row: BlankName): string {
  const canonical = [row.colorName, row.sizeName].filter(Boolean).join(' · ');
  if (canonical) return canonical;
  return row.sampleVariantTitle ?? '';
}

/**
 * Label for a "blanks to pull" row: the canonical brand+style when the resolved inventory name has one,
 * else the caller's Shopify product title. A "nameless" inventory row (no brand/style/colour/size, so
 * {@link blankLabel} falls through to the bare sku) is treated as no name — the readable order-line
 * title wins, so the shop floor never sees a bare machine sku as a blank's label. Shared by both pull
 * lists (gang-sheet pick list + press station) so they name a nameless blank the same way.
 */
export function pullBlankLabel(name: BlankName | undefined, fallbackTitle: string): string {
  const canonical = name ? blankLabel(name) : '';
  return canonical && canonical !== name?.ssSku ? canonical : fallbackTitle;
}

/**
 * Colour · size for a "blanks to pull" row: the canonical colour·size when known, else the caller's
 * Shopify variant title, else empty. Mirrors {@link pullBlankLabel}'s nameless-row fallback.
 */
export function pullBlankVariant(name: BlankName | undefined, fallbackVariant?: string): string {
  const canonical = name ? variantLabel(name) : '';
  return canonical || fallbackVariant || '';
}

/** Money or an em-dash for null. */
export function money(n: number | null): string {
  return n === null ? '—' : `$${n.toFixed(2)}`;
}

/**
 * Invalidate the whole inventory query family after any write. On-hand feeds the demand shortfall, the
 * reorder thresholds AND the draft PO, so every mutation must refresh all of them (they were separate,
 * un-cross-invalidated keys before the hub). `['inventory']` is a prefix that also covers the per-SKU
 * detail query `['inventory', ssSku]`; the overview + draft + purchase-orders keys are distinct strings,
 * so list them. `['purchase-orders']` is likewise a prefix — it also busts the manual builder's
 * `['purchase-orders', 'open-on-order']` query, so the duplicate-open-PO warning refreshes after a create.
 * Purchase-order writes also change the on-order netting, so they refresh the same family.
 */
export function invalidateInventory(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['inventory'] });
  qc.invalidateQueries({ queryKey: ['inventory-overview'] });
  qc.invalidateQueries({ queryKey: ['inventory-purchase-draft'] });
  qc.invalidateQueries({ queryKey: ['inventory-inbound'] });
  qc.invalidateQueries({ queryKey: ['purchase-orders'] });
  // Printed-transfer (film) inventory tab — its own ledger, refreshed on any inventory write.
  qc.invalidateQueries({ queryKey: ['transfer-inventory'] });
  // Finished-garment shelf tab — its own ledger, refreshed the same way.
  qc.invalidateQueries({ queryKey: ['garment-stock'] });
  invalidatePullItems(qc);
}

/** The hub's pull items (pulled-blanks v2 B2, `['count-shelf', 'hub']`): a short answered by a shelf
 *  count, a sheet ticked, a leftover returned — each of those writes must clear its line on the next
 *  hub visit, not five minutes later. Called by {@link invalidateInventory} and by the pull-record doors
 *  (which move no ledger, so they do not call the former). The count page's own frozen `['count-shelf']`
 *  snapshot is NOT touched: the key is the exact sub-key. */
export function invalidatePullItems(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['count-shelf', 'hub'] });
}

/** A style's S&S variant (color × size) — a row of the /garments/by-style/:style/variants payload. */
export type StyleVariant = { sku: string; colorName: string; sizeName: string; qty: number };

// Youth run first (owner default, 2026-08 UI refresh), then adult XS→6XL. Youth tokens are
// DELIBERATELY disjoint from adult ones (YM ≠ M) — mirrors the server blank-resolver's guard
// against a cross-band mismatch.
const SIZE_ORDER = [
  'YXS',
  'YS',
  'YM',
  'YL',
  'YXL',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
];

/** Rank a size for display order; unknown sizes (one-size, M/L, Adjustable, etc.) sink to the end. */
export function sizeRank(s: string): number {
  const i = SIZE_ORDER.indexOf(s.trim().toUpperCase());
  return i === -1 ? SIZE_ORDER.length : i;
}

/** Index of the first ADULT token — everything before it is a youth size. */
const FIRST_ADULT = SIZE_ORDER.indexOf('XS');

/**
 * A size that orders meaningfully (YXS…6XL) — i.e. one worth showing as a badge. Promoted here from
 * `count-shelf-lib`, where it was module-private, because the order card's size chip needs exactly
 * the same predicate and a second copy would be a second thing to keep in step.
 */
export function isRealSize(size: string): boolean {
  return size !== '' && sizeRank(size) < SIZE_ORDER.length;
}

/** A canonical YOUTH token (YXS…YXL). False for anything `isRealSize` rejects. */
export function isYouthSize(size: string): boolean {
  return isRealSize(size) && sizeRank(size) < FIRST_ADULT;
}

/** Alternate spellings → the canonical size token used by `SIZE_ORDER`/`sizeRank`. */
const SIZE_ALIASES: Record<string, string> = {
  'X-SMALL': 'XS',
  XSMALL: 'XS',
  'EXTRA SMALL': 'XS',
  // Youth spellings — same set the server blank-resolver canonicalizes.
  YXSMALL: 'YXS',
  'YOUTH XS': 'YXS',
  'YOUTH X-SMALL': 'YXS',
  'YOUTH XSMALL': 'YXS',
  'YTH XS': 'YXS',
  'YOUTH S': 'YS',
  'YOUTH SMALL': 'YS',
  'YTH S': 'YS',
  'YOUTH M': 'YM',
  'YOUTH MEDIUM': 'YM',
  'YTH M': 'YM',
  'YOUTH L': 'YL',
  'YOUTH LARGE': 'YL',
  'YTH L': 'YL',
  YXLARGE: 'YXL',
  'YOUTH XL': 'YXL',
  'YOUTH X-LARGE': 'YXL',
  'YTH XL': 'YXL',
  SM: 'S',
  SMALL: 'S',
  MED: 'M',
  MEDIUM: 'M',
  LG: 'L',
  LARGE: 'L',
  'X-LARGE': 'XL',
  XLARGE: 'XL',
  'EXTRA LARGE': 'XL',
  XXL: '2XL',
  '2X': '2XL',
  '2XLARGE': '2XL',
  '2X-LARGE': '2XL',
  XXXL: '3XL',
  '3X': '3XL',
  '3XLARGE': '3XL',
  '3X-LARGE': '3XL',
  XXXXL: '4XL',
  '4X': '4XL',
  '5X': '5XL',
  '6X': '6XL',
  // ⚠️ The `-LARGE` spellings above stopped at 3X, and this shop's catalog does not. Measured across
  // both live stores' catalog variants: `4X-Large` and `5X-Large` are the literal `size` values
  // Shopify hands us, and every one of them was falling through to `sizeRank`'s "unknown" bucket —
  // sorting to the end of a pull list, behind YXS, and displaying as the raw seven-character string.
  // The omission is an oversight in the table, not a decision: `2X-LARGE`/`3X-LARGE` are right here.
  '4X-LARGE': '4XL',
  '4XLARGE': '4XL',
  '5X-LARGE': '5XL',
  '5XLARGE': '5XL',
  '6X-LARGE': '6XL',
  '6XLARGE': '6XL',
};

/**
 * Best-effort canonical size token from a raw size or variant string, so sizes order and display
 * consistently regardless of source. Handles the Shopify "Color / Size" variant-title shape (takes the
 * segment after the last " / " — a spaced slash, so a genuine combined size like "L/XL" is left alone),
 * full-word sizes ("Large" → "L"), and the X-notation family ("XXL"/"2X"/"2X-Large" → "2XL"). Returns the
 * trimmed original when it can't confidently map (OSFA, L/XL, youth, one-offs) so nothing is destroyed —
 * those simply sort to the end via `sizeRank`.
 */
export function normalizeSizeName(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const slash = trimmed.lastIndexOf(' / ');
  const seg = (slash === -1 ? trimmed : trimmed.slice(slash + 3)).trim();
  const key = seg.toUpperCase();
  if (SIZE_ORDER.includes(key)) return key;
  return SIZE_ALIASES[key] ?? seg;
}

/**
 * One family of blanks on the merged On-hand list: a brand+style group header over its size rows.
 * Grouping is by brand+STYLE, not brand+style+colour, deliberately (same trade-off as
 * `buildCountSheet`): the shop stocks one colourway of each tee — so colour belongs in the HEADER —
 * while the caps are one size across many colourways, and a colour key would shatter them into
 * one-row groups. `color` carries the promoted colourway when the family has exactly one distinct
 * colour; otherwise null and each row shows its own.
 */
export type BlankFamily<R extends BlankName> = {
  /** Stable identity for React keys — `blankLabel` lowercased. */
  key: string;
  /** "Tultex 602" — or the bare SKU / Shopify title for a nameless blank (a singleton group). */
  label: string;
  /** The family's single colourway, promoted into the header; null when colours vary (caps). */
  color: string | null;
  /** Rows in wearing order (youth run, then XS→6XL; unplaceable sizes last, alphabetical). */
  rows: R[];
  /** Max row severity — >0 means the family needs action and floats to the top. */
  severity: number;
};

/**
 * Group blank rows into families for the merged On-hand list.
 *
 * Row order inside a family is the `buildCountSheet` comparator (size rank → variant text → SKU).
 * FAMILY order preserves the overview's deliberate worst-first contract at group level
 * (inventory-overview.ts sorts needs-action rows up; naive A-Z grouping would bury a short 2XL
 * inside an otherwise-healthy family): families with any needs-action row come first, ordered by
 * their worst row's `severity`, then the rest alphabetically.
 */
export function groupBlankFamilies<R extends BlankName>(
  rows: readonly R[],
  severity: (row: R) => number = () => 0,
): BlankFamily<R>[] {
  const groups = new Map<string, BlankFamily<R>>();

  for (const row of rows) {
    const label = blankLabel(row);
    const key = label.toLowerCase();
    const g = groups.get(key);
    if (g) {
      g.rows.push(row);
      g.severity = Math.max(g.severity, severity(row));
    } else {
      groups.set(key, { key, label, color: null, rows: [row], severity: severity(row) });
    }
  }

  for (const g of groups.values()) {
    g.rows.sort(
      (a, b) =>
        sizeRank(normalizeSizeName(a.sizeName ?? '')) -
          sizeRank(normalizeSizeName(b.sizeName ?? '')) ||
        variantLabel(a).localeCompare(variantLabel(b)) ||
        a.ssSku.localeCompare(b.ssSku),
    );
    const colors = new Set(g.rows.map((r) => r.colorName).filter(Boolean));
    if (colors.size === 1) g.color = [...colors][0] ?? null;
  }

  return [...groups.values()].sort(
    (a, b) =>
      Number(b.severity > 0) - Number(a.severity > 0) ||
      b.severity - a.severity ||
      a.label.localeCompare(b.label),
  );
}

/**
 * The size-grid rows for a chosen color: the color's variants, one per size, in size order.
 *
 * Color is matched case/whitespace-INSENSITIVELY. The color picker's value comes from the
 * by-style color list, which `dedupColors` collapses case-insensitively and emits as the first-seen
 * spelling — so S&S spelling one color inconsistently across its sizes (e.g. "Sport Grey" vs
 * "Sport grey") would otherwise make those sizes silently vanish from a strict `===` grid filter.
 * A size that repeats under one color keeps the first-seen SKU.
 */
export function pickSizeRows(variants: readonly StyleVariant[], color: string): StyleVariant[] {
  const norm = color.trim().toLowerCase();
  const seen = new Set<string>();
  const rows: StyleVariant[] = [];
  for (const v of variants) {
    if (v.colorName.trim().toLowerCase() !== norm) continue;
    const key = v.sizeName.trim().toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(v);
  }
  return rows.sort(
    (a, b) => sizeRank(a.sizeName) - sizeRank(b.sizeName) || a.sizeName.localeCompare(b.sizeName),
  );
}
